import { ref, reactive, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import bus from 'vue3-eventbus';
import { wexinShare, getWechatShareImgUrl } from '@/utils/wxshare.js';
import { navigateNewIndexSearch } from '@/utils/newIndexSearch.js';
import {
  mapIndexNewsList,
  mapIndexSliderList,
  mapTaleList,
  mapHotPicList,
  mapSubjectCatList,
  mapVideoList,
  mapVrList,
  mapCuratedSection,
  mapReleaseList,
  mapInternationalIndex,
} from '@/utils/newIndexMappers';
import { getDailyScene, getWeeklyPick } from '@/api/common';
import { sloganScenes as mockSloganScenes, newsList as mockNewsList, spotCards as mockSpotCards, vrList as mockVrList } from '@/data/newIndexMock.js';
import { USE_NEW_INDEX_MOCK } from '@/config/newIndexDataSource.js';
import NewIndexHeader from '@/components/newIndex/NewIndexHeader.vue';
import NewsCarousel from '@/components/newIndex/NewsCarousel.vue';
import SloganSlider from '@/components/newIndex/SloganSlider.vue';
import SpotsGrid from '@/components/newIndex/SpotsGrid.vue';
import IntCommunicator from '@/components/newIndex/IntCommunicator.vue';
import PhotoDepot from '@/components/newIndex/PhotoDepot.vue';
import VisualQingdao from '@/components/newIndex/VisualQingdao.vue';
import QdRelease from '@/components/newIndex/QdRelease.vue';
import VrExhibition from '@/components/newIndex/VrExhibition.vue';
import AskQingdao from '@/components/newIndex/AskQingdao.vue';
import Footer from '@/components/layout/footer.vue';
import SideComponent from '@/components/common/SideComponent.vue';

export default {
  name: 'new_index',
  components: {
    SideComponent,
    NewIndexHeader,
    NewsCarousel,
    SloganSlider,
    SpotsGrid,
    IntCommunicator,
    PhotoDepot,
    VisualQingdao,
    QdRelease,
    VrExhibition,
    AskQingdao,
    Footer,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const { proxy } = getCurrentInstance();
    const pageRoot = ref(null);

    const showDialog = ref(false);
    const showLogin = ref(false);
    const showMobile = ref(false);

    const checkLoggedIn = () => {
      const isAuthenticated = proxy.$cookies.getAuthKey();
      const token = proxy.$cookies.getToken();
      return !!(
        isAuthenticated &&
        token !== undefined &&
        token !== '' &&
        token != null
      );
    };

    const isLoggedIn = computed(() => checkLoggedIn());

    const username = computed(() => {
      if (!checkLoggedIn()) return '';
      const info = store.state.user.userInfo || {};
      if (info.username && info.username !== 'undefined') return info.username;
      if (info.mobile) return info.mobile;
      return t('anonymity');
    });

    const onLoginDialogChange = (value) => {
      showDialog.value = value.value;
    };

    const openLogin = () => {
      sessionStorage.setItem('redirectPath', route.fullPath);
      showDialog.value = true;
      showMobile.value = false;
      showLogin.value = true;
    };

    const onShowLoginDialog = (e) => { showDialog.value = e; };
    const onShowLoginDialogMobile = (e) => { showMobile.value = e; };
    const onShowLoginDialogLogin = (e) => { showLogin.value = e; };

    const pageData = reactive({
      newsList: [],
      sloganScenes: [],
      spotCards: [],
      hotPhotos: [],
      subjectCats: [],
      visualVideos: [],
      releaseCards: [],
      vrList: [],
      dailyScene: null,
      weeklyPick: null,
      internationalCates: [],
      internationalItems: [],
      loading: true,
    });

    const wxshare = (shareData) => {
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      let url = `${protocol}//${hostname}`;
      if (hostname.indexOf('www') === -1) {
        url = `${protocol}//www.${hostname}`;
      }
      url += route.fullPath;

      store
        .dispatch('common/Jssdk', { url })
        .then((res) => {
          wexinShare(res, {
            title: (shareData.title || '').replace('<br>', ' '),
            desc: shareData.seo_description || '',
            link: url,
            imgUrl: getWechatShareImgUrl(shareData),
          });
        })
        .catch(() => {});
    };

    const loadPageData = async () => {
      pageData.loading = true;
      let siteRes = null;

      const applyInternationalIndex = (res) => {
        const mapped = mapInternationalIndex(res);
        if (mapped.cates.length) {
          pageData.internationalCates = mapped.cates;
        }
        if (mapped.items.length) {
          pageData.internationalItems = mapped.items;
        }
        return mapped;
      };

      const siteInfoPromise = store.dispatch('common/fetchSiteInfo').then((res) => {
        siteRes = res;
        pageData.hotPhotos = mapHotPicList(res.hot1, res.hot2);
        wxshare(res);
        return res;
      });

      const internationalPromise = store
        .dispatch('common/InternationalIndex', { size: 6 })
        .then((res) => applyInternationalIndex(res))
        .catch(() => null);

      const newsPromise = USE_NEW_INDEX_MOCK
        ? Promise.resolve().then(() => {
          pageData.newsList = mockNewsList;
        })
        : store
          .dispatch('common/GetIndexNews', { size: 10 })
          .then((res) => {
            pageData.newsList = mapIndexNewsList(res);
          })
          .catch(() => {
            pageData.newsList = [];
          });

      const sliderPromise = USE_NEW_INDEX_MOCK
        ? Promise.resolve().then(() => {
          pageData.sloganScenes = mockSloganScenes;
        })
        : store
          .dispatch('common/GetIndexSlider')
          .then((res) => {
            const mapped = mapIndexSliderList(res);
            pageData.sloganScenes = mapped.length ? mapped : mockSloganScenes;
          })
          .catch(() => {
            pageData.sloganScenes = mockSloganScenes;
          });

      const talePromise = USE_NEW_INDEX_MOCK
        ? Promise.resolve().then(() => {
          pageData.spotCards = mockSpotCards;
        })
        : store
          .dispatch('common/TaleList', { type: 1, page: 1, size: 4 })
          .then((res) => {
            pageData.spotCards = mapTaleList(res.data, 4);
          })
          .catch(() => {
            pageData.spotCards = [];
          });

      const subjectCatsPromise = store
        .dispatch('common/getSubjectCats', { pid: 0 })
        .then((res) => {
          pageData.subjectCats = mapSubjectCatList(res);
        })
        .catch(() => {
          pageData.subjectCats = [];
        });

      const videoPromise = store
        .dispatch('common/getPicLists', { page: 1, type: 2 })
        .then((res) => {
          pageData.visualVideos = mapVideoList(res);
        })
        .catch(() => {
          pageData.visualVideos = [];
        });

      const releasePromise = store
        .dispatch('common/PublishList', { size: 5 })
        .then((res) => {
          pageData.releaseCards = mapReleaseList(res.data ?? res, 4);
        })
        .catch(() => {
          pageData.releaseCards = [];
        });

      const vrPromise = USE_NEW_INDEX_MOCK
        ? Promise.resolve().then(() => {
          pageData.vrList = mockVrList;
        })
        : store
          .dispatch('common/getVRList')
          .then((res) => {
            pageData.vrList = mapVrList(res);
          })
          .catch(() => {
            pageData.vrList = [];
          });

      const dailyScenePromise = getDailyScene()
        .then((res) => {
          pageData.dailyScene = mapCuratedSection(res);
        })
        .catch(() => {
          pageData.dailyScene = null;
        });

      const weeklyPickPromise = getWeeklyPick()
        .then((res) => {
          pageData.weeklyPick = mapCuratedSection(res);
        })
        .catch(() => {
          pageData.weeklyPick = null;
        });

      await Promise.all([
        siteInfoPromise,
        internationalPromise,
        newsPromise,
        sliderPromise,
        talePromise,
        subjectCatsPromise,
        videoPromise,
        releasePromise,
        vrPromise,
        dailyScenePromise,
        weeklyPickPromise,
      ]);

      if (!pageData.internationalItems.length || !pageData.internationalCates.length) {
        const fallback = mapInternationalIndex(siteRes?.international_index || {});
        if (!pageData.internationalCates.length && fallback.cates.length) {
          pageData.internationalCates = fallback.cates;
        }
        if (!pageData.internationalItems.length && fallback.items.length) {
          pageData.internationalItems = fallback.items;
        }
      }

      pageData.loading = false;
    };

    const handleSearch = (payload) => {
      const query = typeof payload === 'string' ? payload : payload?.query;
      const category = payload?.category || 'news';
      navigateNewIndexSearch(router, store, { query, category });
    };

    const getScrollElement = () => {
      const page = pageRoot.value;
      if (page && page.scrollHeight > page.clientHeight + 1) {
        return page;
      }
      return document.scrollingElement || document.documentElement;
    };

    const scroll = (val) => {
      const el = getScrollElement();
      const viewport = el.clientHeight;
      const maxScroll = Math.max(0, el.scrollHeight - viewport);
      const current = el.scrollTop;

      if (val === 'top') {
        el.scrollTo({
          top: Math.max(0, current - viewport),
          behavior: 'smooth',
        });
      } else if (val === 'bottom') {
        el.scrollTo({
          top: Math.min(maxScroll, current + viewport),
          behavior: 'smooth',
        });
      }
    };

    onMounted(() => {
      sessionStorage.setItem('redirectPath', route.fullPath);
      bus.on('showLoginDialog', onShowLoginDialog);
      bus.on('showLoginDialogMobile', onShowLoginDialogMobile);
      bus.on('showLoginDialogLogin', onShowLoginDialogLogin);
      bus.emit('showFooter', true);
      loadPageData();
    });

    onUnmounted(() => {
      bus.off('showLoginDialog', onShowLoginDialog);
      bus.off('showLoginDialogMobile', onShowLoginDialogMobile);
      bus.off('showLoginDialogLogin', onShowLoginDialogLogin);
      bus.emit('showFooter', false);
    });

    return {
      pageRoot,
      pageData,
      handleSearch,
      scroll,
      showDialog,
      showLogin,
      showMobile,
      isLoggedIn,
      username,
      openLogin,
      onLoginDialogChange,
    };
  },
};
